/**
 * The contact-sales callback modal's analytics vocabulary: the `action` tokens
 * its three CTAs carry, the payload shapes its two events report, and the emit
 * seams the app binds.
 *
 * This is the terminal surface of the contact-sales path — the recap modal, the
 * trial-ended modal and the 90-day upgrade-modal variant all route here — so its
 * own `screen_name` is a single token and the origin never travels into the
 * payload. What the funnel joins on is the origin's `..._contact_sales` click,
 * which each of those surfaces already reports.
 */
import type {
  TRIALS_AND_UPGRADES_SCREEN,
  TrialPlanContext,
} from './trials-and-upgrades-analytics.types';

/** The `action` tokens the callback modal's three CTAs carry. */
export const TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION = {
  send: 'trial_upgrade_request_callback_send',
  cancel: 'trial_upgrade_request_callback_cancel',
  back: 'trial_upgrade_request_callback_back',
} as const;

export type TrialUpgradeRequestCallbackActionToken =
  (typeof TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION)[keyof typeof TRIAL_UPGRADE_REQUEST_CALLBACK_ACTION];

/** `modal_viewed` on the callback modal. Fired once per open. */
export type TrialUpgradeRequestCallbackViewedProps = {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['trialUpgradeRequestCallback'];
};

/** `button_clicked` on any of the callback modal's three CTAs. */
export type TrialUpgradeRequestCallbackClickProps = TrialPlanContext & {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['trialUpgradeRequestCallback'];
  action: TrialUpgradeRequestCallbackActionToken;
  label: string;
};

/** The emit seam for the callback modal becoming visible. */
export type TrackTrialUpgradeRequestCallbackViewed = (
  props: TrialUpgradeRequestCallbackViewedProps
) => void;

/** The emit seam for a callback-modal CTA. */
export type TrackTrialUpgradeRequestCallbackClick = (
  props: TrialUpgradeRequestCallbackClickProps
) => void;

/**
 * The two seams as one app-supplied bundle.
 *
 * Held as two independently stable functions rather than read off this object:
 * `trackViewed` is a dependency of the open effect, so a port rebuilt each render
 * would re-fire it.
 */
export type TrialUpgradeRequestCallbackAnalyticsPorts = {
  trackViewed: TrackTrialUpgradeRequestCallbackViewed;
  trackClick: TrackTrialUpgradeRequestCallbackClick;
};
